// src/lib/bridge/bridgeAuth.ts
import { z } from 'zod';

/**
 * Bridge Auth via BFF/Gateway
 * - Безопасный вызов через внутренний роут (не светим ключи провайдера)
 * - Валидация ответа Zod
 * - Таймаут + backoff + идемпотентность
 * - Локальный in-memory кэш токена (с упреждением по времени)
 *
 * Требуемый маршрут BFF/Gateway:
 *   POST /api/gateway-v1/bridge/auth
 *   → 200 { token: string, expiresAt: number } // expiresAt в unix секундах
 */

const AUTH_ENDPOINT = '/api/gateway-v1/bridge/auth' as const;

// ---- Типы результата ---------------------------------------------------------

export type BridgeAuthOk = { token: string; expiresAt: number };
export type BridgeAuthErrCode =
  | 'INVALID_CREDENTIALS'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'RATE_LIMIT'
  | 'TIMEOUT'
  | 'NETWORK'
  | 'BAD_RESPONSE'
  | 'SERVER';

export type BridgeAuthErr = { code: BridgeAuthErrCode; detail?: string };

export type BridgeAuthResult =
  | { ok: true; data: BridgeAuthOk }
  | { ok: false; error: BridgeAuthErr };

// ---- Схемы ответа -----------------------------------------------------------

const AuthResp = z.object({
  token: z.string().min(1),
  // unix timestamp (seconds). допускаем число/строку-число (жёстко приводим к number)
  expiresAt: z.union([z.number().int(), z.string().regex(/^\d+$/)]).transform((v) => Number(v)),
});

// ---- Утилиты: таймаут, бэкофф, идемпотентность -------------------------------

function withTimeout<T>(p: Promise<T>, ms: number, msg = 'timeout'): Promise<T> {
  if (typeof AbortController !== 'undefined') {
    const c = new AbortController();
    const t = setTimeout(() => c.abort(), ms);
    return Promise.race([
      p.catch((e) => {
        // пробрасываем как есть
        throw e;
      }),
      new Promise<never>((_, rej) =>
        c.signal.addEventListener('abort', () => rej(new Error(msg)))
      ),
    ]).finally(() => clearTimeout(t));
  }
  // Фолбэк без AbortController
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(msg)), ms);
    p.then((v) => {
      clearTimeout(t);
      resolve(v);
    }).catch((e) => {
      clearTimeout(t);
      reject(e);
    });
  });
}

type BackoffOpts = { tries: number; baseMs: number; factor?: number; jitter?: boolean };
async function backoff<T>(fn: () => Promise<T>, opts: BackoffOpts): Promise<T> {
  const factor = opts.factor ?? 2;
  for (let i = 0; i < opts.tries; i++) {
    try {
      return await fn();
    } catch (e) {
      if (i === opts.tries - 1) throw e;
      const delay =
        opts.baseMs * Math.pow(factor, i) *
        (opts.jitter ? 0.5 + Math.random() : 1);
      await new Promise((r) => setTimeout(r, delay));
    }
  }
  // теоретически недостижимо
   
  throw new Error('backoff_exhausted');
}

function idemKey(): string {
  // Дешёвый идемпотентный ключ для POST-запроса
  // Устраивает в рантайме браузера/ноды без зависимостей
  const rnd = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `idem-${rnd}`;
}

// ---- Локальный кэш токена ----------------------------------------------------

let _cached: BridgeAuthOk | null = null;
/** Секунды упреждения: за столько до expiresAt токен считаем просроченным */
const SKEW_SEC = 30;

/** Сбрасывает кэш токена вручную (на случай 401 от провайдера) */
export function invalidateBridgeToken() {
  _cached = null;
}

function isFresh(entry: BridgeAuthOk | null, nowSec = Math.floor(Date.now() / 1000)): boolean {
  if (!entry) return false;
  return entry.expiresAt - SKEW_SEC > nowSec;
}

// ---- Основной вызов ----------------------------------------------------------

export async function bridgeAuthenticate(): Promise<BridgeAuthResult> {
  // если в кэше есть свежий — вернём его без сетки
  if (isFresh(_cached)) {
    return { ok: true, data: _cached as BridgeAuthOk };
  }

  try {
    const data = await backoff(
      async () => {
        const r = await withTimeout(
          fetch(AUTH_ENDPOINT, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Idempotency-Key': idemKey(),
            },
            credentials: 'include', // используем сессию, если требуется в BFF
          }),
          7000,
          'timeout'
        );

        if (!r.ok) {
          // маппинг по статусам — не раскрываем подробностей наружу
          if (r.status === 401) throw new BridgeAuthHttpError('UNAUTHORIZED', r.status);
          if (r.status === 403) throw new BridgeAuthHttpError('FORBIDDEN', r.status);
          if (r.status === 429) throw new BridgeAuthHttpError('RATE_LIMIT', r.status);
          if (r.status >= 500) throw new BridgeAuthHttpError('SERVER', r.status);
          throw new BridgeAuthHttpError('BAD_RESPONSE', r.status);
        }

        const json = await r.json().catch(() => {
          throw new Error('bad_json');
        });
        const parsed = AuthResp.parse(json);
        const payload: BridgeAuthOk = {
          token: parsed.token,
          expiresAt: parsed.expiresAt,
        };
        _cached = payload;
        return payload;
      },
      { tries: 3, baseMs: 300, factor: 2, jitter: true }
    );

    return { ok: true, data };
  } catch (e) {
    const err = normalizeError(e);
    return { ok: false, error: err };
  }
}

/**
 * Возвращает валидный токен, гарантируя авто-обновление при просрочке.
 * Если обновить не удалось — кидает (для удобства в вызовах, где ошибка критична).
 */
export async function ensureBridgeToken(): Promise<string> {
  const now = Math.floor(Date.now() / 1000);
  if (!isFresh(_cached, now)) {
    const res = await bridgeAuthenticate();
    if (!res.ok) throw new Error(`bridge_auth_failed:${res.error.code}`);
  }
   
  return _cached!.token;
}

// ---- Вспомогательные ошибки/нормализация ------------------------------------

class BridgeAuthHttpError extends Error {
  public readonly tag = 'BridgeAuthHttpError';
  constructor(public readonly code: BridgeAuthErrCode, public readonly http: number) {
    super(`${code}:${http}`);
  }
}

function normalizeError(e: unknown): BridgeAuthErr {
  if (e instanceof BridgeAuthHttpError) {
    return { code: e.code, detail: String(e.http) };
  }
  if (e instanceof Error) {
    if (e.message === 'timeout') return { code: 'TIMEOUT' };
    if (e.message === 'bad_json') return { code: 'BAD_RESPONSE' };
    // сетевые/прочие
    return { code: guessNetworkCode(e.message), detail: e.message };
  }
  return { code: 'NETWORK' };
}

function guessNetworkCode(msg: string): BridgeAuthErrCode {
  if (/ECONNREFUSED|ENOTFOUND|NetworkError/i.test(msg)) return 'NETWORK';
  if (/429/.test(msg)) return 'RATE_LIMIT';
  if (/401/.test(msg)) return 'UNAUTHORIZED';
  if (/403/.test(msg)) return 'FORBIDDEN';
  if (/5\d\d/.test(msg)) return 'SERVER';
  return 'NETWORK';
}